'use client';

import React, { useState } from 'react';
import { AlertTriangle, Info, AlertCircle, Zap, X } from 'lucide-react';
import type { NetworkAlert } from '@/hooks/useDashboardRealtime';
import Icon from '@/components/ui/AppIcon';


interface Props {
  alerts: NetworkAlert[];
  suppressCount: number;
  loading: boolean;
  onDismiss?: (id: string) => void;
}

const severityConfig = {
  p1: { icon: AlertTriangle, color: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/20' },
  p2: { icon: AlertCircle, color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' },
  p3: { icon: Info, color: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
};

export default function AlertFeed({ alerts, suppressCount, loading, onDismiss }: Props) {
  const [localDismissed, setLocalDismissed] = useState<Set<string>>(new Set());

  const dismiss = (id: string) => {
    setLocalDismissed((prev) => new Set([...prev, id]));
    onDismiss?.(id);
  };

  const visible = alerts.filter((a) => !localDismissed.has(a.id));

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } catch {
      return iso;
    }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} className="text-warning" />
          <p className="text-sm font-semibold text-foreground">Live Alert Feed</p>
          {!loading && (
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="flex items-center gap-1 text-success">
            <Zap size={10} />
            {suppressCount} suppressed
          </span>
          <span className="text-muted">AI correlated</span>
        </div>
      </div>

      <div className="space-y-2 overflow-y-auto flex-1 scrollbar-hide" style={{ maxHeight: 380 }}>
        {loading && visible.length === 0 && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-surface-raised border border-border rounded-lg p-2.5 animate-pulse h-12" />
            ))}
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="flex items-center justify-center h-24 text-muted text-xs">
            No active alerts
          </div>
        )}
        {visible.map((alert) => {
          const cfg = severityConfig[alert.severity] ?? severityConfig.p3;
          const Icon = cfg.icon;
          return (
            <div
              key={alert.id}
              className={`${cfg.bg} ${cfg.border} border rounded-lg p-2.5 flex items-start gap-2.5 group hover:border-opacity-50 transition-all duration-150 animate-fade-in`}
            >
              <Icon size={13} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-foreground font-medium leading-snug">{alert.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-[10px] text-muted">{alert.node_name}</span>
                  <span className="text-[10px] text-muted">·</span>
                  <span className="text-[10px] text-primary font-semibold">{alert.ai_confidence}% AI</span>
                  {alert.correlated && (
                    <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold">
                      CORRELATED
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-mono text-[10px] text-muted whitespace-nowrap">
                  {formatTime(alert.created_at)}
                </span>
                <button
                  onClick={() => dismiss(alert.id)}
                  className="opacity-0 group-hover:opacity-100 text-muted hover:text-foreground transition-all"
                >
                  <X size={11} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}